import styled from '@emotion/styled'
import { Button, Modal, notification } from 'antd'
import { useMemo, useState } from 'react'
import { useAccount, useBalance, useChainId, useWriteContract } from 'wagmi'

import error_icon from '@/assets/error_icon.png'
import success_icon from '@/assets/success_icon.png'
import { config } from '@/config'
import { useLocalStorage } from '@/context/localstorage-context'
import type {
  StoremanStakeInfoType,
  WanscanVldAttrType,
} from '@/types/storeman-type'
import { formatBalance, fromWei, ImgCreator } from '@/utils'

import { Link } from '../stroeman-detail.module'

export default function ClaimModal({
  visible,
  onCancel,
  storemanStakeInfo,
  vld_attr,
  unclaimedData,
}: {
  visible: boolean
  unclaimedData: string
  onCancel: () => void
  vld_attr: WanscanVldAttrType
  storemanStakeInfo: StoremanStakeInfoType
}) {
  const chainId = useChainId()
  const { address } = useAccount()
  const { data, status } = useBalance({ address })
  const [loadingBTn, setLoadingBtn] = useState(false)
  const { theme } = useLocalStorage()
  const isdark = useMemo(() => theme === 'dark', [theme])
  const { data: hash, writeContractAsync } = useWriteContract()
  const StoremanContractConfig = config[chainId]['StoremanContract']

  const balance = useMemo(() => {
    return data && status === 'success'
      ? formatBalance(data.value.toString(10))
      : '0'
  }, [data, status])

  const onOk = async () => {
    setLoadingBtn(true)
    try {
      const hash = await writeContractAsync({
        address: StoremanContractConfig.addr,
        abi: StoremanContractConfig.abi,
        functionName: storemanStakeInfo.canStakeClaim
          ? 'stakeClaim'
          : 'stakeIncentiveClaim',
        args: [storemanStakeInfo.wkAddr],
      })
      notification.success({
        message: 'Transaction completed',
        description: <Link txHash={hash} />,
        className: `${isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'}`,
        icon: <img width={30} src={success_icon} alt="notification" />,
      })
      onCancel()
    } catch (error) {
      console.error('StoremanClaim', error)
      notification.error({
        message: 'Transaction Sent Failed',
        description: <Link txHash={hash || ''} />,
        className: `${isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'}`,
        icon: <img width={30} src={error_icon} alt="notification" />,
      })
      onCancel()
    }
  }

  return (
    <Modal
      className={isdark ? 'darkModal' : 'lightModal'}
      maskClosable={true}
      closable={true}
      destroyOnClose={true}
      style={{ background: '#051F31', borderRadius: '16px' }}
      width={700}
      mask={true}
      open={visible}
      title={<Title className="text-light-0 dark:text-white">Claim</Title>}
      footer={null}
      onCancel={onCancel}
    >
      <Part isdark={isdark} className="bg-white dark:bg-common-1">
        <PartTitle className="text-light-0 dark:text-white">
          Bridge Node Account
        </PartTitle>
        <LineStyle isdark={isdark} />
        <DetailInfo>
          <RowStyle>
            <span>Name</span>
            <AddrText>
              {vld_attr.iconData && (
                <ImgStyle
                  width={25}
                  src={ImgCreator(vld_attr.iconData, vld_attr.iconType)}
                  alt="icon"
                />
              )}
              {vld_attr.name}
            </AddrText>
          </RowStyle>
          <RowStyle>
            <span>Address</span>
            <AddrText>{storemanStakeInfo.wkAddr}</AddrText>
          </RowStyle>
          <RowStyle>
            <span>Reward</span>
            <AddrText>{fromWei(storemanStakeInfo.incentive)}</AddrText>
          </RowStyle>
          <RowStyle>
            <span>Claimable Amount</span>
            <AddrText>{unclaimedData}</AddrText>
          </RowStyle>
        </DetailInfo>
      </Part>
      <Part isdark={isdark} className="bg-white dark:bg-common-1">
        <PartTitle className="text-light-0 dark:text-white">
          Wallet Account
        </PartTitle>
        <LineStyle isdark={isdark} />
        <DetailInfo>
          <RowStyle>
            <span>Amount</span>
            <span>
              <span style={{ color: '#2FBDF4' }}>{balance} </span>
              WAN
            </span>
          </RowStyle>
          <RowStyle>
            <span>Address</span>
            <AddrText>{address}</AddrText>
          </RowStyle>
        </DetailInfo>
      </Part>
      <BtnArea isdark={isdark}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={onOk} loading={loadingBTn}>
          Confirm
        </Button>
      </BtnArea>
    </Modal>
  )
}

const Title = styled.span`
  font-size: 24px;
  font-family: Inter-Bold, Inter;
  font-weight: bold;
`

const BtnArea = styled.div<{
  isdark: boolean
}>`
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  gap: 20px;

  .ant-btn:first-of-type {
    background: ${(props) =>
      props.isdark ? 'rgba(255, 255, 255, 0.1)' : '#E5E5E5'};
    border-radius: 16px;
    color: ${(props) => (props.isdark ? '#818d96' : '#999')};
  }
  .ant-btn:first-of-type:hover {
    background: ${(props) =>
      props.isdark ? 'rgba(255, 255, 255, 0.1)' : '#E5E5E5'};
  }
  .ant-btn {
    width: calc((100% - 20px) / 2);
    border-radius: 16px;
    height: 60px;
    color: white;
    border: none;
    background: #0f68aa;
  }
  .ant-btn:hover {
    background: #0f68aa;
  }
`

const Part = styled.div<{
  isdark: boolean
}>`
  border-radius: 16px;
  opacity: 1;
  border: ${(props) =>
    props.isdark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #EBEFF2'};
  margin-bottom: 2rem;
  padding-top: 2rem;

  span {
    color: #818d96;
  }
`

const PartTitle = styled.div`
  font-size: 16px;
  font-family: Inter-Regular, Inter;
  font-weight: 400;
  line-height: 19px;
  background-clip: text;
  margin-left: 2rem;
  text-align: left;
`

const LineStyle = styled.div<{
  isdark: boolean
}>`
  width: 100%;
  margin: 2rem 0;
  opacity: 0.2;
  border: ${(props) =>
    props.isdark ? '1px solid #263B4A' : '1px solid #EBEFF2'};
`

const DetailInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  padding: 0 2rem 2rem;
  gap: 1rem;
`

const RowStyle = styled.div`
  color: #757575;
  display: flex;
  justify-content: space-between;

  p {
    font-size: 16px;
  }
`

const ImgStyle = styled.img`
  border-radius: 50%;
  border: 1px solid #757575;
  margin-right: 3px;
  display: inline-block;
`

const AddrText = styled.span`
  @media (max-width: 768px) {
    width: 70%;
    white-space: break-spaces;
    text-align: right;
  }
`
